<template>

    <div class="flex flex-col-reverse md:flex-row justify-between">

        <!-- Menu -->
        <aside class="relative md:h-screen overflow-y-auto shrink-0 md:max-w-xs">
            <div class="max-w-full px-3 py-5">

                <h4 class="tracking-wide font-lato-bold color-primary text-center pb-2">{{ book?.title }}</h4>

                <div v-if="book?.chapters > 0" id="chapter-boxes" class="text-center pb-5">
                    <a
                        v-for="i in parseInt(book.chapters)"
                        @click.prevent="loadBookChapter(i); scrollToVerse(1)"
                        :class="{'active-chapter': i == chapter}"
                        :href="`/${book.division}/${book.url}/${i}/`"
                    >
                        {{ i }}
                    </a>
                </div>

                <book-list />
            </div>
        </aside>

        <!-- Scripture -->
        <div class="classic-reading-background md:container mx-auto relative">
            <div v-if="contentLoading" class="flex items-center justify-center blur-background absolute inset-0 z-10" style="background: rgba(230,230,230,0.3)">
                <div class="animate-spin inline-block w-16 h-16 border-4 rounded-full" style="border-color: rgba(13, 59, 105, 0.4); border-right-color: transparent;"></div>
            </div>

            <div class="px-10 pt-7 pb-10 overflow-y-auto h-screen" id="reading-content">
                <h1 class="text-4xl mb-4">{{ book?.original }}</h1>

                <h3 id="chapter-title" class="mb-3 text-lg">{{ chapter }}. nodaļa</h3>

                <div v-for="note in chapterNotes.top" class="chapter-notes">
                    <div v-html="note"></div>
                </div>

                <div class="clear-both">
                    <ol id="scripture">
                        <li v-for="i in verseCount" :id="'verse-' + i" class="clear-both">
                            <bdo v-html="hebrewText[i]"></bdo>
                            <div>
                                <sup>{{ i }}</sup>

                                {{ latvianText[i] }}

                                <div v-if="verseNotes[i]">
                                    <a href="javascript:" class="notes-btn closed" @click="isNoteOpen[i] = true">
                                        Rādīt komentārus
                                    </a>
                                </div>
                            </div>
                        </li>
                    </ol>
                </div>

                <div v-for="note in chapterNotes.bottom" class="chapter-notes">
                    <div v-html="note"></div>
                </div>

                <div class="pt-6 flex flex-row justify-between clear-both color-primary">
                    <button v-if="book && chapter > 1" @click="loadBookChapter(chapter - 1); scrollToVerse(1)" class="text-xl flex flex-row justify-center items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" style="fill: currentColor; transform: scaleX(-1);"><path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/></svg>
                        {{ chapter - 1 }}
                    </button>
                    <span v-else></span>

                    <button v-show="book && chapter < book.chapters" @click="loadBookChapter(chapter + 1); scrollToVerse(1)" class="text-xl flex flex-row justify-center items-center">
                        {{ chapter + 1 }}
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" style="fill: currentColor;"><path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/></svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Tulkojumi -->
        <aside v-show="translationsPaneVisible" class="absolute md:relative top-0 right-0 w-full blur-background h-screen overflow-y-auto shrink-0" style="min-width: 16rem;">
            <div class="max-w-full px-4 py-2">
                <dictionary />
            </div>
        </aside>
    </div>

</template>

<script>

    import { ref, reactive, watch, provide, nextTick } from 'vue'
    import { initializeHebrew, checkAvailability } from '~/hebrew.js'

    import BookList from './BookList.vue'
    import Dictionary from './Dictionary.vue'

    export default {
        components: {
            BookList,
            Dictionary
        },
        setup() {
            const book = ref(null)
            const chapter = ref(null)

            const chapterNotes = reactive({
                top: [],
                bottom: [],
            })

            const dictionary = reactive({
                visible: false,
                selectedWord: null,
                translations: []
            })

            const verseCount = ref(0)
            const verseNotes = ref([])
            const isNoteOpen = ref([])

            const latvianText = ref([])
            const hebrewText = ref([])

            const menuVisible = ref(false)
            const translationsPaneVisible = ref(false)

            const contentLoading = ref(false)



            function loadBookChapter(chapterNumber, bookUrl = null, pushHistory = true) {
                contentLoading.value = true
                
                axios.post('/api/scripture/get-chapter', {
                    _token: window.csrfToken,
                    bookUrl: bookUrl ?? book.value?.url,
                    chapter: chapterNumber
                }).then(response => {
                    const chapterData = response.data

                    let url = `/${chapterData.book.division}/${chapterData.book.url}/${chapterNumber}/`
                    let title = `${chapterNumber}. nodaļa`
                    let targetVerseNumber = 1

                    renderChapter(chapterData)

                    if (pushHistory) {
                        history.pushState(chapterData, title, url)

                    } else {
                        if (window.location.hash.length > 1) {
                            targetVerseNumber = parseInt(window.location.hash.substr(1))
                        }

                        history.replaceState(chapterData, title, url)
                    }

                    nextTick(() => {
                        scrollToVerse(targetVerseNumber)
                    })

                }).then(() => {
                    contentLoading.value = false
                })
            }


            function renderChapter(chapterData) {
                if (chapterData == null) {
                    return
                }

                // var scripture = document.getElementById("scripture")
                // var errorbtn = document.getElementById("report-error-btn")


                // errorbtn.href = errorbtn.href.replace(/\/\d+\/?$/, "/" + chapterData.chapter + "/")

                // if (chapterData.error) {
                //     scripture.previousElementSibling.innerHTML = "Norādītā grāmata nav atrasta"
                //     return
                // }

                // if (chapterData.hebrew.length == 0) {
                //     scripture.previousElementSibling.innerHTML = "Šajā grāmatā nav " + chapterData.chapter + ". nodaļa"
                // }

                book.value = chapterData.book

                chapter.value = chapterData.chapter

                latvianText.value = chapterData.latvian
                hebrewText.value = chapterData.hebrew

                const latvianVersesCount = Object.keys(latvianText.value).length
                const hebrewVersesCount = Object.keys(hebrewText.value).length

                verseCount.value = latvianVersesCount > hebrewVersesCount ? latvianVersesCount : hebrewVersesCount


                chapterNotes.top = []
                chapterNotes.bottom = []

                for (var i = 0; i < chapterData.chapter_notes.length; i++) {
                    chapterNotes[chapterData.chapter_notes[i].position].push(chapterData.chapter_notes[i].content)
                }


                nextTick(() => {
                    initializeHebrew("bdo, .hebrew-script")

                    // pirmā nodaļa pilnībā iztulkota
                    if (book.value.url != "beresit" || chapter.value != 1) {
                        checkAvailability("bdo .hebrew-word")
                    }
                })
            }


            function scrollToVerse(verseNumber) {
                if (verseNumber) {
                    const container = document.getElementById('reading-content')

                    if (verseNumber == 1) {
                        container.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        })
                    } else {
                        const verseElem = document.getElementById('verse-' + verseNumber)

                        if (! verseElem) {
                            return
                        }

                        const targetY = verseElem.getBoundingClientRect().top

                        container.scrollTo({
                            top: targetY,
                            left: 0,
                            behavior: 'smooth'
                        })
                    }
                }
            }


            const [ , initialBookUrl, initialChapter] = window.location.pathname.replace(/^\/|\/$/g, '').split('/')

            loadBookChapter(initialChapter, initialBookUrl, false)

            provide('loadBookChapter', loadBookChapter)
            provide('translationsPaneVisible', translationsPaneVisible)


            window.onpopstate = e => renderChapter(e.state)

            return {
                book,
                chapter,

                dictionary,

                chapterNotes,
                verseCount,
                verseNotes,
                isNoteOpen,

                menuVisible,
                translationsPaneVisible,

                contentLoading,

                latvianText,
                hebrewText,

                loadBookChapter,
                scrollToVerse,
            }
        }
    }
</script>

<style lang="scss">

    h1 {
        font-family: 'TaameyFrank', serif;
        text-align: center;
    }

    #chapter-title {
        text-align: center;
        direction: ltr;
    }

    #scripture {
        direction: rtl;
        padding-right: 20px;
        list-style-type: hebrew;
        text-align: right;
        float: right;

        li {
            line-height: 30px;
            font-family: 'TaameyFrank', serif;
            font-weight: bold;
            margin-bottom: 10px;
            position: relative;

            & > bdo {
                font-family: 'TaameyFrank', serif;
                font-weight: normal;
                display: inline-block;
                vertical-align: top;
                padding-top: 5px;
                font-size: 1.7rem;
                line-height: 1.9rem;
                direction: rtl;
                width: 50%;
                color: #111;
            }

            & > div {
                direction: ltr;
                width: 50%;
                font: normal 14px/18px Lato;
                text-align: left;
                padding: 10px 5px 0 0;
                max-height: 100%;
                box-sizing: border-box;
                font-size: 1.1rem;
                line-height: 1.5rem;
                float: left;
                color: #333;
                
                & > sup:first-child {
                    margin-right: 5px;
                }
            }
        }
    }

    #chapter-boxes {
        font-size: 0;

        a {
            display: inline-block;
            text-align: center;
            background: #ccc;
            color: #333;
            border-radius: 0;
            width: 32px;
            height: 32px;
            padding: 0;
            font-size: 15px;
            line-height: 32px;
            margin: 0 3px 3px 0;
            box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);

            &.active-chapter {
                background: #0d3b69;
                color: #fff;
            }
        }
    }


    .unknown-hebrew-word {
        color: #777;
    }

    .hebrew-word {
        cursor: help;

        &:hover {
            color: #2D5B89;
        }
    }


    .book-link {
        display: block;
        text-decoration: none;
        line-height: 22px;
        height: 24px;
        color: #2D5B89;

        .hebrew {
            font-size: 1.7rem;
            font-family: TaameyFrank, serif;
            line-height: 1.9rem;
            float: right;
            color: #333;
            display: inline-block;
            vertical-align: middle;
        }
    }

    #chapter-boxes {
        font-size: 0;

        button {
            background: #ccc;
            color: #333;
            border-radius: 0;
            width: 32px;
            height: 32px;
            padding: 0;
            font-size: 15px;
            line-height: 32px;
            margin: 0 3px 3px 0;

            &.active-chapter {
                background: #0d3b69;
                color: #fff;
            }
        }
    }

    #prev-chapter-btn, #next-chapter-btn {
        position: absolute;
        bottom: 15px;
        z-index: 5;
        background: none;
        font-size: 18px;
        font-family: Lato;
        color: #0d3b69;
    }
    #prev-chapter-btn {
        left: 25px;
    }
    #next-chapter-btn {
        right: 25px;
    }

    .notes-btn {
        background: none;
        font-size: 15px;
        font-family: Vollkorn;
        color: #4682B4;
        text-decoration: none;
        padding: 5px 5px 5px 12px;
        margin-top: 5px;
        display: inline-block;
    }

    .notes-btn.open::before {
        content: '';
        display: block;
        position: absolute;
        margin-top: 7px;
        margin-left: -12px;
        width: 7px;
        height: 55px;
        border-top: 1px solid #bbb;
        border-left: 1px solid #bbb;
    }

    .chapter-notes {
        padding: 20px 0;
        font: normal 14px/18px Lato;
        text-align: left;
        color: #444;

        h4 {
            font-size: 15px;
            font-family: Vollkorn;
            text-align: center;
        }

        p {
            margin-bottom: 5px;
        }
    }

    @media (min-width: 600px) {
        aside.h-screen {
            width: 20rem;
        }
    }

</style>